import React from 'react';
import { Link } from 'react-router-dom';
import { 
    MapPin, 
    ArrowRight, 
    FileText, 
    BarChart3,
    HelpCircle
} from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Search Result Card - Legal Compliance Version
 * 
 * Shows address with review count and summary availability indicator.
 * Does NOT display risk badges, risk levels, or risk terminology.
 * 
 * Displays:
 * - Address
 * - Number of contractor reviews
 * - "Contractor data available" or "No contractor data yet"
 * 
 * LOGIC: review_count >= 1 = contractor data exists
 */
const SearchResultCard = ({ address, onClick }) => {
    const reviewCount = address.active_review_count || address.review_count || 0;
    // Any review (>= 1) means contractor data exists
    const hasReviews = reviewCount >= 1;
    
    // Summary availability configuration (NOT risk badges)
    // Summary available when at least 1 review exists
    const getSummaryConfig = () => {
        if (hasReviews) {
            return {
                badge: 'bg-primary/20 text-primary border-primary/30',
                label: 'Data available',
                icon: BarChart3,
                showScore: true
            };
        }
        
        return {
            badge: 'bg-muted/30 text-muted-foreground border-border',
            label: 'No data yet',
            icon: HelpCircle,
            showScore: false
        };
    };
    
    const config = getSummaryConfig();
    const Icon = config.icon;
    
    const CardContent = (
        <div 
            className={cn(
                "flex items-center justify-between p-4 sm:p-4 rounded-xl border transition-all duration-200",
                "bg-accent/30 border-border hover:border-primary/50 hover:bg-accent/50",
                "cursor-pointer group min-h-[72px]"  // Ensure minimum tap target height
            )}
            onClick={onClick}
            data-testid={`search-result-${address.id}`}
        >
            {/* Left: Address Info */}
            <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="p-2.5 rounded-lg bg-primary/10 flex-shrink-0">
                    <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate text-base">
                        {address.display_address || address.street_address}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                        {address.city}{address.state && `, ${address.state}`} {address.zip_code}
                    </p>
                    {/* Review count indicator */}
                    <div className="flex items-center gap-2 mt-1.5">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                            {reviewCount} contractor review{reviewCount !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>
            </div>
            
            {/* Right: Summary Status Badge + Arrow */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2 sm:ml-3">
                {/* Summary Availability Badge (NOT Risk Badge) */}
                <div className={cn(
                    "flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-full text-xs font-medium border",
                    config.badge
                )}>
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{config.label}</span>
                </div>
                
                {/* Arrow - larger tap target */}
                <div className="p-2 -mr-2 rounded-full">
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
            </div>
        </div>
    );
    
    // If onClick is provided, use div. Otherwise wrap in Link
    if (onClick) {
        return CardContent;
    }
    
    return (
        <Link to={`/property/${address.id}`} className="block">
            {CardContent}
        </Link>
    );
};

/**
 * Empty State Components for Search
 */
export const SearchEmptyState = ({ searchQuery, onAddNew }) => (
    <div className="text-center py-10" data-testid="search-empty-state">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
            <MapPin className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="font-semibold text-foreground mb-2">No addresses found</h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
            We couldn't find any addresses matching "<span className="font-medium">{searchQuery}</span>" in our database.
        </p>
        {onAddNew && (
            <button
                onClick={onAddNew}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors"
                data-testid="add-new-address-btn"
            >
                <MapPin className="h-4 w-4" />
                Review Property Now
            </button>
        )}
    </div>
);

export const NoDataYetState = ({ address }) => (
    <div className="text-center py-8" data-testid="no-data-state">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
            <HelpCircle className="h-7 w-7 text-primary" />
        </div>
        <h3 className="font-semibold text-foreground mb-1">No contractor reviews yet</h3>
        <p className="text-sm text-muted-foreground">
            Be the first to review this property
        </p>
    </div>
);

// Deprecated - no longer used since threshold was removed
export const UnderThresholdState = ({ reviewCount }) => (
    <div className="text-center py-8" data-testid="under-threshold-state">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
            <FileText className="h-7 w-7 text-primary" />
        </div>
        <h3 className="font-semibold text-foreground mb-1">Contractor job history grows as contractors log experiences</h3>
        <p className="text-sm text-muted-foreground">
            {reviewCount} contractor review{reviewCount !== 1 ? 's' : ''} logged
        </p>
    </div>
);

export default SearchResultCard;
